/**
 * Silent Token Refresh Hook
 * Proactively refreshes JWT tokens before they expire
 * Prevents sudden "session expired" errors and hard page reloads
 *
 * CRITICAL: Uses backend API health check instead of Supabase getSession()
 * to avoid timeout issues when tab is inactive
 */

import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { apiClient } from '@/shared/services/apiClient';
import { getAccessToken } from '@/shared/utils/supabaseStorage';

const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry
const CHECK_INTERVAL_MS = 2 * 60 * 1000; // Check every 2 minutes

/**
 * Hook to automatically refresh tokens before expiration
 * Runs in background without user interaction
 *
 * @example
 * // In App.tsx or AuthContext
 * useTokenRefresh();
 */
export function useTokenRefresh() {
  const refreshInProgressRef = useRef(false);
  const lastRefreshTimeRef = useRef(0);

  /**
   * Check if token needs refresh and refresh if necessary
   * Uses backend API call instead of Supabase getSession to avoid hangs
   */
  const checkAndRefreshToken = useCallback(async () => {
    // Prevent concurrent refresh attempts
    if (refreshInProgressRef.current) {
      console.log('[TokenRefresh] Refresh already in progress, skipping');
      return;
    }

    // Don't refresh more than once per minute
    const timeSinceLastRefresh = Date.now() - lastRefreshTimeRef.current;
    if (timeSinceLastRefresh < 60 * 1000) {
      console.log('[TokenRefresh] Refreshed recently, skipping');
      return;
    }

    try {
      refreshInProgressRef.current = true;

      // CRITICAL FIX: Get token from localStorage using shared utility
      const accessToken = getAccessToken();

      if (!accessToken) {
        console.log('[TokenRefresh] No access token found');
        refreshInProgressRef.current = false;
        return;
      }

      // Make lightweight backend API health check
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      const response = await fetch(`${apiClient.baseURL}/health`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.log('[TokenRefresh] Health check failed, token may be expired');

        // Try to refresh the token
        console.log('[TokenRefresh] 🔄 Refreshing token silently...');
        const { data, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) {
          console.error('[TokenRefresh] ❌ Refresh failed:', refreshError.message);
          // Don't throw - let the next API call handle it
        } else if (data.session) {
          console.log('[TokenRefresh] ✅ Token refreshed successfully');
          lastRefreshTimeRef.current = Date.now();
        }
      } else {
        console.log('[TokenRefresh] ✅ Token is valid');
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('[TokenRefresh] Health check timed out after 3s');
      } else {
        console.error('[TokenRefresh] ❌ Error during token check:', error.message);
      }
      // Don't throw - this is a background operation
    } finally {
      refreshInProgressRef.current = false;
    }
  }, []);

  // Set up interval to check token expiration
  useEffect(() => {
    console.log('[TokenRefresh] 🚀 Silent token refresh enabled');

    // Check immediately on mount
    checkAndRefreshToken();

    // Then check periodically
    // NO NEED to pause when tab is hidden - health check is lightweight and won't hang
    const interval = setInterval(checkAndRefreshToken, CHECK_INTERVAL_MS);

    return () => {
      console.log('[TokenRefresh] 🛑 Silent token refresh disabled');
      clearInterval(interval);
    };
  }, [checkAndRefreshToken]);
}
